import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { ControlContainer, FormBuilder } from '@angular/forms';
import { ChecklistDatabase, TreeItemFlatNode, TreeItemNode } from './ChecklistDatabase';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { FlatTreeControl } from '@angular/cdk/tree';
import { SelectionModel } from '@angular/cdk/collections';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";

@Component({
	selector: 'app-region-council-tree-list',
	templateUrl: './region-council-tree-list.component.html',
	styleUrls: ['./region-council-tree-list.component.scss']
})

 
export class RegionCouncilTreeListComponent implements OnInit, OnDestroy {

	treeData$ : BehaviorSubject<TreeItemNode[]> = new BehaviorSubject<TreeItemNode[]>([]);

	@Input()
	get treeData()
	{
		return this.treeData$.value;
	}

	set treeData (value : TreeItemNode[])
	{
		this.treeData$.next(value);
	}
	
	flatNodeMap = new Map<TreeItemFlatNode, TreeItemNode>();	
	nestedNodeMap = new Map<TreeItemNode, TreeItemFlatNode>();	
	selectedParent: TreeItemFlatNode | null = null;	

	treeControl : FlatTreeControl<TreeItemFlatNode>;	
	treeFlattener: MatTreeFlattener<TreeItemNode, TreeItemFlatNode>;
	treeDataSource: MatTreeFlatDataSource<TreeItemNode, TreeItemFlatNode>;	
	checklistSelection = new SelectionModel<TreeItemFlatNode>(true /* multiple */);


	private dataSub : Subscription;

  	constructor(private fb: FormBuilder,
		private controlContainer: ControlContainer,	
		private matIconRegistry: MatIconRegistry,	
		private domSanitizer: DomSanitizer) 
	{
		this.registerIcons();
		this.treeFlattener = new MatTreeFlattener(
			this.transformer,
			this.getLevel,
			this.isExpandable,
			this.getChildren,
		);
		this.treeControl = new FlatTreeControl<TreeItemFlatNode>(this.getLevel, this.isExpandable);
		this.treeDataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);	

	}

	private registerIcons = () => {
		this.matIconRegistry.addSvgIcon("chevronDown", 
		this.domSanitizer.bypassSecurityTrustResourceUrl("assets/images/SystemIcons/chevron-down-solid.svg"));
		this.matIconRegistry.addSvgIcon("chevronRight", 
			this.domSanitizer.bypassSecurityTrustResourceUrl("assets/images/SystemIcons/chevron-right-solid.svg"));
	}

	ngOnInit(): void {
		this.dataSub = this.treeData$.subscribe({
			next : (data : any[]) => {
				this.treeDataSource.data = data;
			},
			error : (ex : Error) => {

			},
			complete : () => {

			}
		})
	}

	ngOnDestroy(): void {
		this.dataSub && this.dataSub?.unsubscribe();
	}	

	getLevel = (node: TreeItemFlatNode) => node.level;

	isExpandable = (node: TreeItemFlatNode) => node.expandable;

	getChildren = (node: TreeItemNode): TreeItemNode[] => node.children;

	hasChild = (_: number, _nodeData: TreeItemFlatNode) => _nodeData.expandable;

	isShown = (node: TreeItemFlatNode) => !node.hidden;

	hasNoContent = (_: number, _nodeData: TreeItemFlatNode) => _nodeData.label === '';
	
	transformer = (node: TreeItemNode, level: number) => 
	{
		const existingNode = this.nestedNodeMap.get(node);
		const flatNode =
			existingNode && existingNode.id === node.id ? existingNode : new TreeItemFlatNode();
		flatNode.id = node.id;
		flatNode.label = node.label;
		flatNode.level = level;
		flatNode.expandable = !!node.children?.length;
		this.flatNodeMap.set(flatNode, node);
		this.nestedNodeMap.set(node, flatNode);
		return flatNode;
	};

	
	descendantsAllSelected(node: TreeItemFlatNode): boolean {
		const descendants = this.treeControl.getDescendants(node);
		const descAllSelected =
			descendants.length > 0 &&
			descendants.every(child => {
				return this.checklistSelection.isSelected(child);
			});
		return descAllSelected;
	}
	
	descendantsPartiallySelected(node: TreeItemFlatNode): boolean {
		const descendants = this.treeControl.getDescendants(node);
		const result = descendants.some(child => this.checklistSelection.isSelected(child));
		return result && !this.descendantsAllSelected(node);
	}
	
	treeItemSelectionToggle(node: TreeItemFlatNode): void {
		this.checklistSelection.toggle(node);
		const descendants = this.treeControl.getDescendants(node);
		this.checklistSelection.isSelected(node)
			? this.checklistSelection.select(...descendants)
			: this.checklistSelection.deselect(...descendants);

		
		descendants.forEach(child => this.checklistSelection.isSelected(child));
		this.checkAllParentsSelection(node);
	}
	
	treeLeafItemSelectionToggle(node: TreeItemFlatNode): void {
		this.checklistSelection.toggle(node);
		this.checkAllParentsSelection(node);
	}

	
	checkAllParentsSelection(node: TreeItemFlatNode): void {
		let parent: TreeItemFlatNode | null = this.getParentNode(node);
		while (parent) {
			this.checkRootNodeSelection(parent);
			parent = this.getParentNode(parent);
		}
	}
	
	checkRootNodeSelection(node: TreeItemFlatNode): void {
		const nodeSelected = this.checklistSelection.isSelected(node);
		const descendants = this.treeControl.getDescendants(node);
		const descAllSelected =
			descendants.length > 0 &&
			descendants.every(child => {
				return this.checklistSelection.isSelected(child);
			});
		if (nodeSelected && !descAllSelected) {
			this.checklistSelection.deselect(node);
		} else if (!nodeSelected && descAllSelected) {
			this.checklistSelection.select(node);
		}
	}
	
	getParentNode(node: TreeItemFlatNode): TreeItemFlatNode | null {
		const currentLevel = this.getLevel(node);

		if (currentLevel < 1) {
			return null;
		}

		const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

		for (let i = startIndex; i >= 0; i--) {
			const currentNode = this.treeControl.dataNodes[i];

			if (this.getLevel(currentNode) < currentLevel) {
				return currentNode;
			}
		}
		return null;
	}
}


